/* home-v2.jsx — new main page: ecosystem hero with correlation view, Code/Infra/Threats
 * lenses, integrated funnel, updated pipeline + roadmap.
 * Loaded after components.js; overrides HomePage (same pattern as threat-architecture.jsx).
 * Reuses Frameworks and CTA/Footer from components.jsx.
 */

function ChainNode({ rail, name, sub, live }) {
  const border = rail ? `1px solid var(--border)` : "1px solid var(--border)";
  return (
    <div style={{border: rail ? `1px solid color-mix(in oklab, ${rail} 35%, transparent)` : border, borderLeft: rail && rail.startsWith("var(--red)") ? undefined : undefined, borderRadius:8, padding:"8px 10px", background:"var(--surface)", display:"grid", gap:2}}>
      <span className="mono" style={{fontSize:10.5, color:"var(--text)"}}>{name} {live && <span className="dot red" style={{width:4, height:4}}/>}</span>
      <span className="mono" style={{fontSize:9, color:"var(--text-4)"}}>{sub}</span>
    </div>
  );
}

function ChainConnector() {
  return (
    <svg viewBox="0 0 22 220" preserveAspectRatio="none" style={{width:"100%", height:"100%"}} aria-hidden="true">
      <path d="M0 38 L22 40" stroke="var(--teal)" strokeOpacity="0.5" strokeWidth="1.25" fill="none"/>
      <path d="M0 110 L22 112" stroke="var(--teal)" strokeOpacity="0.5" strokeWidth="1.25" fill="none"/>
      <path d="M0 182 L22 184" stroke="var(--teal)" strokeOpacity="0.5" strokeWidth="1.25" fill="none"/>
      <circle cx="11" cy="39" r="1.8" fill="var(--teal)"/>
      <circle cx="11" cy="111" r="1.8" fill="var(--teal)"/>
      <circle cx="11" cy="183" r="1.8" fill="var(--teal)"/>
    </svg>
  );
}

function CorrelationConsole() {
  const code = [
    { name:"aws-access-key-id", sub:"payments-api · deploy_legacy.sh:41", rail:"var(--red)" },
    { name:"log4j-core 2.14.1", sub:"checkout-web · 2.14.1 → 2.17.2", rail:"var(--red)" },
    { name:"http_tokens = optional", sub:"infra-terraform · ec2.tf:55", rail:"var(--orange)" },
  ];
  const infra = [
    { name:"payments-api", sub:"internet (proxied) · PII" },
    { name:"checkout-web", sub:"internet · revenue-critical" },
    { name:"web-prod-1", sub:"internet (direct) · SSH open" },
  ];
  const threats = [
    { name:"Credential misuse", sub:"key used from unknown ASN · 41m", rail:"var(--red)", live:true },
    { name:"Log4Shell attempts", sub:"214 callbacks blocked · ongoing", rail:"var(--red)" },
    { name:"SSH brute force", sub:"1,900 attempts · 2 hosts", rail:"var(--orange)" },
  ];
  const chip = (color, n, label) => (
    <span className="mono" style={{fontSize:10.5, color:"var(--text-2)", border:"1px solid var(--border)", borderRadius:999, padding:"3px 10px"}}>
      <span style={{color}}>{n}</span> {label}
    </span>
  );
  const col = (label, items) => (
    <div style={{display:"grid", gap:8, alignContent:"start"}}>
      <span className="mono" style={{fontSize:9, letterSpacing:"0.12em", color:"var(--text-4)"}}>{label}</span>
      {items.map((it,i)=><ChainNode key={i} {...it} />)}
    </div>
  );
  return (
    <div className="console" aria-label="AtmosGuard correlation view, mock UI" style={{minHeight:0, alignSelf:"center"}}>
      <div className="console-bar">
        <span className="dot live" />
        <span className="path">atmosguard</span><span className="sep">/</span><span className="path">correlation</span><span className="sep">/</span><span className="path">risk chains</span>
        <div className="right"><span>last run <span style={{color:"var(--text-2)"}}>05:58 UTC</span></span><span className="kbd">⌘K</span></div>
      </div>
      <div style={{padding:"14px 16px", display:"grid", gap:12}}>
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          {chip("var(--red)", "5", "active threats")}
          {chip("var(--teal)", "4/5", "traced to code")}
          {chip("var(--green)", "6", "fixable with a PR today")}
        </div>
        <div style={{display:"grid", gridTemplateColumns:"1fr 22px 1fr 22px 1fr", alignItems:"stretch"}}>
          {col("CODE · FIX HERE", code)}
          <ChainConnector />
          {col("INFRASTRUCTURE", infra)}
          <ChainConnector />
          {col("THREATS · ACTIVE", threats)}
        </div>
        <div style={{borderTop:"1px dashed var(--border)", paddingTop:9, fontSize:11, color:"var(--text-3)"}}>Every active threat traced back to its source. The workload it hits, the misconfiguration that exposes it, and the line of code that fixes it.</div>
      </div>
    </div>
  );
}

/* ---------- Hero: board report panel (executive view, illustrative numbers) ---------- */
function BoardReport() {
  const greyBar = (x) => <rect key={x} x={x} y="4" width="14" height="28" rx="2" fill="var(--text-4)" opacity=".5" />;
  return (
    <div className="board" aria-label="Board report, illustrative">
      <div className="board-head"><span className="board-title">Board report · Q4</span><span className="board-meta">Illustrative</span></div>
      <div className="board-top">
        <div>
          <div className="board-eyebrow">Open exposures · 12 weeks</div>
          <div className="board-big">−41%</div>
        </div>
        <div className="board-legend"><span><i style={{background:"var(--teal)"}} />WITH ATMOSGUARD</span><span><i style={{background:"var(--text-4)"}} />BEFORE</span></div>
      </div>
      <svg className="board-chart" viewBox="0 0 560 140" aria-hidden="true">
        <defs><linearGradient id="boardFill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="var(--teal)" stopOpacity="0.28" /><stop offset="1" stopColor="var(--teal)" stopOpacity="0" /></linearGradient></defs>
        <g stroke="var(--border)" strokeWidth="1"><line x1="0" y1="28" x2="560" y2="28" /><line x1="0" y1="66" x2="560" y2="66" /><line x1="0" y1="104" x2="560" y2="104" /></g>
        <path d="M0 40 L47 44 L93 38 L140 46 L187 42 L233 48" fill="none" stroke="var(--text-4)" strokeWidth="1.5" strokeDasharray="4 4" />
        <path d="M233 48 L280 60 L327 68 L373 80 L420 88 L467 98 L513 104 L560 110 L560 140 L233 140 Z" fill="url(#boardFill)" />
        <path d="M233 48 L280 60 L327 68 L373 80 L420 88 L467 98 L513 104 L560 110" fill="none" stroke="var(--teal)" strokeWidth="2.2" strokeLinejoin="round" />
        <line x1="233" y1="18" x2="233" y2="140" stroke="var(--teal)" strokeOpacity="0.5" strokeDasharray="3 3" />
        <circle cx="233" cy="48" r="3.5" fill="var(--teal)" />
        <circle cx="560" cy="110" r="3.5" fill="var(--teal)" />
        <text x="238" y="14" fontFamily="JetBrains Mono" fontSize="9.5" fill="var(--teal)" letterSpacing="1">ONBOARDED</text>
        <text x="0" y="136" fontFamily="JetBrains Mono" fontSize="9" fill="var(--text-4)" letterSpacing="1">JUL</text>
        <text x="275" y="136" fontFamily="JetBrains Mono" fontSize="9" fill="var(--text-4)" letterSpacing="1">AUG</text>
        <text x="530" y="136" fontFamily="JetBrains Mono" fontSize="9" fill="var(--text-4)" letterSpacing="1">OCT</text>
      </svg>
      <div className="board-tvl">
        <div className="board-eyebrow">Time to value</div>
        <svg viewBox="0 0 560 50" aria-hidden="true" style={{height:50, marginTop:6}}>
          <line x1="10" y1="12" x2="550" y2="12" stroke="var(--border)" strokeWidth="2" />
          <line x1="10" y1="12" x2="470" y2="12" stroke="var(--teal)" strokeOpacity="0.6" strokeWidth="2" />
          <circle cx="10" cy="12" r="4" fill="var(--teal)" />
          <circle cx="240" cy="12" r="4" fill="var(--teal)" />
          <circle cx="470" cy="12" r="4.5" fill="var(--teal)" />
          <text x="10" y="32" fontFamily="JetBrains Mono" fontSize="11.5" fill="var(--text)" letterSpacing="0.5">3 min</text>
          <text x="10" y="45" fontFamily="JetBrains Mono" fontSize="9" fill="var(--text-4)" letterSpacing="1">ONBOARDED</text>
          <text x="240" y="32" fontFamily="JetBrains Mono" fontSize="11.5" fill="var(--text)" letterSpacing="0.5">5 min</text>
          <text x="240" y="45" fontFamily="JetBrains Mono" fontSize="9" fill="var(--text-4)" letterSpacing="1">FIRST RESULTS</text>
          <text x="470" y="32" fontFamily="JetBrains Mono" fontSize="11.5" fill="var(--teal)" letterSpacing="0.5">minutes</text>
          <text x="470" y="45" fontFamily="JetBrains Mono" fontSize="9" fill="var(--text-4)" letterSpacing="1">FIX MERGED</text>
        </svg>
      </div>
      <div className="board-tiles">
        <div className="board-tile">
          <div className="board-eyebrow">Tools replaced</div>
          <div className="board-v">6 <small>→ 1 view</small></div>
          <svg viewBox="0 0 120 36" preserveAspectRatio="none" aria-hidden="true" style={{height:36}}>
            {[0,18,36,54,72,90].map(greyBar)}
            <rect x="108" y="4" width="12" height="28" rx="2" fill="var(--teal)" />
          </svg>
        </div>
        <div className="board-tile">
          <div className="board-eyebrow">Signals in → decisions out</div>
          <svg viewBox="0 0 300 78" aria-hidden="true" style={{height:78, marginTop:10}}>
            <rect x="0" y="0" width="300" height="10" rx="3" fill="var(--text-4)" opacity=".35" />
            <text x="0" y="25" fontFamily="JetBrains Mono" fontSize="9.5" fill="var(--text-3)" letterSpacing="1">38,204 RAW SIGNALS</text>
            <rect x="0" y="31" width="96" height="10" rx="3" fill="var(--teal)" opacity=".5" />
            <text x="0" y="56" fontFamily="JetBrains Mono" fontSize="9.5" fill="var(--text-3)" letterSpacing="1">1,017 CORRELATED</text>
            <rect x="0" y="62" width="8" height="10" rx="3" fill="var(--teal)" />
            <text x="14" y="71" fontFamily="JetBrains Mono" fontSize="9.5" fill="var(--teal)" letterSpacing="1">12 WORTH YOUR TIME</text>
          </svg>
        </div>
        <div className="board-tile board-ring">
          <div className="board-eyebrow">Audit ready</div>
          <svg viewBox="0 0 100 100" aria-hidden="true" style={{width:84, height:84, marginTop:4}}>
            <circle cx="50" cy="50" r="40" fill="none" stroke="var(--border)" strokeWidth="9" />
            <circle cx="50" cy="50" r="40" fill="none" stroke="var(--green)" strokeWidth="9" strokeLinecap="round" strokeDasharray="236 251" transform="rotate(-90 50 50)" />
            <text x="50" y="47" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="17" fill="var(--text)">94%</text>
            <text x="50" y="62" textAnchor="middle" fontFamily="JetBrains Mono" fontSize="7.5" fill="var(--text-4)" letterSpacing="1">6 FRAMEWORKS</text>
          </svg>
        </div>
      </div>
      <div className="board-foot"><span>Onboarded in 3 minutes. First results in 5. Fixes merged in minutes, not months.</span><span className="board-meta">illustrative</span></div>
    </div>
  );
}

function HeroV2() {
  return (
    <section id="top" className="hero">
      <div className="wrap-wide hero-grid">
        <div>
          <span className="hero-kicker"><span className="dot live" />One platform · in private beta</span>
          <h1>
            One security ecosystem<br />
            <span style={{color:"var(--teal)"}}>for your whole cloud.</span>
            <span className="mono-sub">code · infrastructure · threats · compliance · one graph, one console</span>
          </h1>
          <p className="subcopy">
            Point tools find point problems. AtmosGuard connects the dots. The code you ship, the
            infrastructure it runs on, and the threats that hit it all live in <span style={{color:"var(--text)"}}>one graph</span>,
            so a single fix in code closes the misconfiguration and the active threat downstream.
            AWS + Azure, read-only, in private beta with a small group of pilot teams.
          </p>
          <div className="hero-cta">
            <a className="btn primary lg" href="https://app.atmosguard.io/register">Start your free trial</a>
            <a className="btn lg" href={L("products/forge.html")}>See the product</a>
            <span className="trust">AWS + Azure · read-only by default</span>
          </div>
        </div>
        <BoardReport />
      </div>
    </section>
  );
}

function StatsV2() {
  return (
    <section className="section">
      <div className="wrap-wide">
        <div className="stats-strip">
          <Stat label="PRODUCTS ON ONE GRAPH" val="4" delta="→ one risk engine, one console" dcls="teal" sub="minimal integration work to get running" />
          <Stat label="SIGNALS EVALUATED TODAY" val="1,017" delta="→ 12 worth a look" dcls="teal" sub="weighed by risk, exposure, and who can reach it" />
          <Stat label="THREATS TRACED TO CODE" val="4 /5" delta="the fix is a PR, not a ticket" dcls="up" sub="traced across the whole chain" />
          <Stat label="NOISE SUPPRESSED" val="−96%" delta="of signals never reach an analyst" dcls="teal" sub="the rest sit in the graph, queryable" />
        </div>
      </div>
    </section>
  );
}

function LensBars() {
  const rows = [
    { label:"code · SAST", pct:100, value:"1,286" },
    { label:"deps · SCA", pct:86, value:"1,111" },
    { label:"infrastructure", pct:73, value:"941" },
    { label:"secrets", pct:6, value:"74" },
  ];
  return (
    <div style={{display:"grid", gap:10, width:"100%"}}>
      {rows.map((r,i)=>(
        <div key={i} style={{display:"grid", gridTemplateColumns:"96px 1fr 40px", alignItems:"center", gap:8}}>
          <span className="mono" style={{fontSize:10, color:"var(--text-3)"}}>{r.label}</span>
          <span style={{height:3, borderRadius:2, background:"var(--bg-2)", position:"relative", overflow:"hidden"}}>
            <span style={{position:"absolute", inset:0, width:r.pct+"%", background:"linear-gradient(90deg,var(--teal),var(--teal-2))"}}/>
          </span>
          <span className="mono" style={{fontSize:10.5, color:"var(--text-2)", textAlign:"right"}}>{r.value}</span>
        </div>
      ))}
    </div>
  );
}

function LensMap() {
  return (
    <svg viewBox="0 0 300 96" style={{width:"100%", height:"auto"}} aria-hidden="true">
      <path d="M92 26 C 112 26, 116 30, 132 30" stroke="var(--teal)" strokeOpacity="0.5" strokeWidth="1.25" fill="none"/>
      <path d="M92 70 C 112 70, 116 42, 132 40" stroke="var(--teal)" strokeOpacity="0.5" strokeWidth="1.25" fill="none"/>
      <path d="M212 35 C 228 35, 232 30, 244 30" stroke="var(--amber)" strokeOpacity="0.6" strokeWidth="1.25" fill="none"/>
      <path d="M212 40 C 228 40, 232 66, 244 66" stroke="var(--teal)" strokeOpacity="0.5" strokeWidth="1.25" fill="none"/>
      <rect x="8" y="10" width="84" height="30" rx="6" fill="var(--surface-2)" stroke="var(--border-2)"/>
      <text x="18" y="24" fontFamily="var(--mono)" fontSize="9.5" fill="var(--text)">web-alb</text>
      <text x="18" y="34" fontFamily="var(--mono)" fontSize="7.5" fill="var(--amber)">DRIFT · 3</text>
      <rect x="8" y="54" width="84" height="30" rx="6" fill="var(--surface-2)" stroke="var(--border-2)"/>
      <text x="18" y="68" fontFamily="var(--mono)" fontSize="9.5" fill="var(--text)">public-api</text>
      <text x="18" y="78" fontFamily="var(--mono)" fontSize="7.5" fill="var(--text-4)">GATEWAY · 4</text>
      <rect x="132" y="18" width="80" height="30" rx="6" fill="var(--surface-2)" stroke="var(--border-2)"/>
      <text x="142" y="32" fontFamily="var(--mono)" fontSize="9.5" fill="var(--text)">checkout</text>
      <text x="142" y="42" fontFamily="var(--mono)" fontSize="7.5" fill="var(--red)">FINDINGS · 7</text>
      <rect x="244" y="14" width="50" height="30" rx="6" fill="var(--surface-2)" stroke="var(--border-2)"/>
      <text x="252" y="28" fontFamily="var(--mono)" fontSize="9.5" fill="var(--text)">s3</text>
      <text x="252" y="38" fontFamily="var(--mono)" fontSize="7.5" fill="var(--amber)">DRIFT</text>
      <rect x="244" y="52" width="50" height="30" rx="6" fill="var(--surface-2)" stroke="var(--border-2)"/>
      <text x="252" y="66" fontFamily="var(--mono)" fontSize="9.5" fill="var(--text)">rds</text>
      <text x="252" y="76" fontFamily="var(--mono)" fontSize="7.5" fill="var(--text-4)">3</text>
    </svg>
  );
}

function LensFeed() {
  const rows = [
    { t:"05:58:41", sev:"HIGH", c:"var(--red)", msg:<>Credential misuse · <span className="hl">payments-api</span> · unknown ASN</> },
    { t:"05:57:12", sev:"HIGH", c:"var(--red)", msg:<>Log4Shell attempts · <span className="hl">checkout-web</span> · 214 blocked</> },
    { t:"05:52:03", sev:"MED", c:"var(--amber)", msg:<>SSH brute force · <span className="hl">web-prod-1</span> · 1,900 attempts</> },
    { t:"05:44:56", sev:"MED", c:"var(--amber)", msg:<>Anonymous S3 enumeration · <span className="hl">atmos-media-uploads</span></> },
  ];
  return (
    <div className="feed">
      {rows.map((r,i)=>(
        <div key={i} className="feed-row">
          <span className="t">{r.t}</span>
          <span className="sev" style={{color:r.c}}>{r.sev}</span>
          <span className="msg">{r.msg}</span>
        </div>
      ))}
    </div>
  );
}

function LensesV2() {
  const M = ({v, k}) => (<div className="m"><div className="v">{v}</div><span className="micro k">{k}</span></div>);
  return (
    <section id="product" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 01 — Product</span></div>
            <h2>Three lenses on the same risk, from code to infrastructure to threats.</h2>
            <p style={{marginTop:12}}>Every lens reads from the same graph and the same risk engine. Open any one and you see the few risks that would actually hurt today, with the trail back to the layer where the fix is cheapest.</p>
          </div>
          <div className="micro" style={{color:"var(--text-3)"}}>ONE GRAPH · RANKED BY RISK</div>
        </div>
        <div className="modules">
          <a className="module" href={L("products/forge.html")} style={{textDecoration:"none"}}>
            <div className="mod-head"><ProductLogo kind="appsec" /><span className="pill teal">Forge</span><span className="micro" style={{marginLeft:"auto"}}>FORGE · APPSEC</span></div>
            <h3>The flaw, caught where it is cheapest to fix.</h3>
            <p className="mod-desc">Code flaws, leaked secrets, risky dependencies, and vulnerable packages, scanned on every push and ranked by what actually runs in production.</p>
            <div className="mod-metrics"><M v="3,412" k="OPEN FINDINGS"/><M v="412" k="REACHABLE IN PROD"/><M v="1" k="CLICK TO FIX PR"/></div>
            <div className="mod-vis"><LensBars /></div>
          </a>
          <a className="module" href={L("products/atlas.html")} style={{textDecoration:"none"}}>
            <div className="mod-head"><ProductLogo kind="posture" /><span className="pill teal">Atlas</span><span className="micro" style={{marginLeft:"auto"}}>ATLAS · CNAPP</span></div>
            <h3>Every resource, mapped to what it can reach.</h3>
            <p className="mod-desc">742 resources and 1,893 relationships drawn as one live map, with misconfigurations and drift flagged on the exact resource they expose.</p>
            <div className="mod-metrics"><M v="742" k="RESOURCES MAPPED"/><M v="1,893" k="RELATIONSHIPS"/><M v="96%" k="NOISE SUPPRESSED"/></div>
            <div className="mod-vis"><LensMap /></div>
          </a>
          <a className="module" href={L("products/sentry.html")} style={{textDecoration:"none"}}>
            <div className="mod-head"><ProductLogo kind="threat" /><span className="pill amber">Sentry · SOON</span><span className="micro" style={{marginLeft:"auto"}}>SENTRY · THREAT INTELLIGENCE</span></div>
            <h3>The attack, traced back to the line of code.</h3>
            <p className="mod-desc">Active detections correlated against the map and the code behind it. Four of five live threats trace to a repo, where the fix is a pull request.</p>
            <div className="mod-metrics"><M v="5" k="ACTIVE THREATS"/><M v="4/5" k="TRACED TO CODE"/><M v="7m 42s" k="TYPICAL TIME-TO-CLOSE"/></div>
            <div className="mod-vis"><LensFeed /></div>
          </a>
        </div>
      </div>
    </section>
  );
}

function ArchitectureDiagramV2() {
  const src = (y, name, sub, dot) => (<g>
    <rect x="20" y={y} width="190" height="56" rx="8" fill="var(--surface)" stroke="var(--border-2)"/>
    <text x="36" y={y+22} fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">{name}</text>
    <text x="36" y={y+40} fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">{sub}</text>
    {dot && <circle cx="196" cy={y+28} r="4" fill={dot}/>}
  </g>);
  const out = (y, name, sub) => (<g>
    <rect x="490" y={y} width="170" height="58" rx="8" fill="var(--surface)" stroke="var(--border-2)"/>
    <text x="506" y={y+22} fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">{name}</text>
    <text x="506" y={y+40} fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">{sub}</text>
  </g>);
  return (
    <div className="cov-diagram" style={{margin:0}}>
      <svg viewBox="0 0 680 380" width="100%" style={{display:"block"}}>
        <defs>
          <linearGradient id="archLineV2" x1="0" x2="1"><stop offset="0%" stopColor="var(--teal)" stopOpacity="0.05"/><stop offset="50%" stopColor="var(--teal)" stopOpacity="0.7"/><stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05"/></linearGradient>
          <linearGradient id="archLine2V2" x1="0" x2="1"><stop offset="0%" stopColor="var(--teal)" stopOpacity="0.7"/><stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05"/></linearGradient>
          <radialGradient id="archGlowV2" cx="50%" cy="50%"><stop offset="0%" stopColor="var(--teal)" stopOpacity="0.18"/><stop offset="100%" stopColor="var(--teal)" stopOpacity="0"/></radialGradient>
        </defs>
        <text x="30" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">SOURCES</text>
        <text x="270" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">CONTROL PLANE</text>
        <text x="510" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">OUTPUTS</text>
        {src(56, "Code", "GitHub app · 42 repos", "var(--teal)")}
        {src(124, "AWS", "812 accounts · 4 regions", "var(--aws)")}
        {src(192, "Azure", "472 subs · 3 regions", "var(--azure)")}
        {src(260, "Identity", "Entra ID · IAM · SAML 2.0")}
        <circle cx="340" cy="190" r="110" fill="url(#archGlowV2)"/>
        <rect x="250" y="130" width="180" height="120" rx="12" fill="var(--surface-2)" stroke="var(--teal)" strokeOpacity="0.4"/>
        <text x="268" y="158" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">AtmosGuard</text>
        <text x="268" y="176" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.2" fill="var(--text-4)">ONE GRAPH</text>
        <line x1="268" y1="188" x2="412" y2="188" stroke="var(--border)"/>
        <text x="268" y="206" fontFamily="var(--mono)" fontSize="10" fill="var(--text-2)">inventory · code map</text>
        <text x="268" y="222" fontFamily="var(--mono)" fontSize="10" fill="var(--text-2)">risk engine · gated AI</text>
        <text x="268" y="238" fontFamily="var(--mono)" fontSize="10" fill="var(--text-2)">correlation · policy</text>
        <circle cx="420" cy="190" r="3" fill="var(--teal)"/><circle cx="420" cy="190" r="10" fill="var(--teal)" opacity="0.18"/>
        {out(70, "Fix in code", "PRs · appsec · vuln")}
        {out(160, "Fix in cloud", "posture · drift · threats")}
        {out(250, "Attest", "evidence · signed")}
        <path d="M 210 84 C 230 84, 235 160, 250 160" stroke="url(#archLineV2)" strokeWidth="1.25" fill="none"/>
        <path d="M 210 152 C 230 152, 235 180, 250 180" stroke="url(#archLineV2)" strokeWidth="1.25" fill="none"/>
        <path d="M 210 220 C 230 220, 235 200, 250 200" stroke="url(#archLineV2)" strokeWidth="1.25" fill="none"/>
        <path d="M 210 288 C 230 288, 235 220, 250 220" stroke="url(#archLineV2)" strokeWidth="1.25" fill="none"/>
        <path d="M 430 160 C 460 160, 470 99, 490 99" stroke="url(#archLine2V2)" strokeWidth="1.25" fill="none"/>
        <path d="M 430 190 C 460 190, 470 189, 490 189" stroke="url(#archLine2V2)" strokeWidth="1.25" fill="none"/>
        <path d="M 430 220 C 460 220, 470 279, 490 279" stroke="url(#archLine2V2)" strokeWidth="1.25" fill="none"/>
        <circle cx="222" cy="120" r="1.8" fill="var(--teal)"/><circle cx="222" cy="255" r="1.8" fill="var(--teal)"/>
        <circle cx="460" cy="130" r="1.8" fill="var(--teal)"/><circle cx="460" cy="250" r="1.8" fill="var(--teal)"/>
        <text x="30" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">read-only by default</text>
        <text x="270" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">graph refreshed every 90s</text>
        <text x="490" y="352" fontFamily="var(--mono)" fontSize="9" fill="var(--text-3)">Slack · Teams · Email · dashboards</text>
      </svg>
    </div>
  );
}

function FunnelDiagramV2() {
  const dots = [];
  for (let r = 0; r < 7; r++) for (let c = 0; c < 16; c++) dots.push(<circle key={r*16+c} cx={48+c*10} cy={196+r*14} r="1.4"/>);
  return (
    <div className="cov-diagram" style={{margin:0}}>
      <svg viewBox="0 0 680 380" width="100%" style={{display:"block"}}>
        <defs>
          <linearGradient id="fline2V2" x1="0" x2="1"><stop offset="0%" stopColor="var(--teal)" stopOpacity="0.7"/><stop offset="100%" stopColor="var(--teal)" stopOpacity="0.05"/></linearGradient>
          <linearGradient id="funnelFillV2" x1="0" x2="1"><stop offset="0%" stopColor="var(--teal)" stopOpacity="0.05"/><stop offset="100%" stopColor="var(--teal)" stopOpacity="0.18"/></linearGradient>
        </defs>
        <text x="30" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">EVERYTHING</text>
        <text x="230" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">RAW SIGNALS</text>
        <text x="410" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">CORRELATED</text>
        <text x="572" y="32" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.4" fill="var(--text-4)">WORTH FIXING</text>
        <path d="M 30 60 L 220 60 L 220 320 L 30 320 Z" fill="url(#funnelFillV2)" opacity="0.4"/>
        <path d="M 220 60 L 400 110 L 400 270 L 220 320 Z" fill="url(#funnelFillV2)" opacity="0.6"/>
        <path d="M 400 110 L 560 160 L 560 220 L 400 270 Z" fill="url(#funnelFillV2)" opacity="0.85"/>
        <rect x="30" y="60" width="190" height="260" rx="10" fill="var(--surface)" stroke="var(--border-2)"/>
        <text x="46" y="86" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Code + cloud inventory</text>
        <text x="46" y="104" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">repos · aws + azure</text>
        <text x="46" y="148" fontFamily="var(--mono)" fontSize="30" fontWeight="600" fill="var(--text)" letterSpacing="-0.02em">38,204</text>
        <text x="46" y="166" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.2" fill="var(--text-4)">RESOURCES · REPOS</text>
        <g fill="var(--text-4)" opacity="0.55">{dots}</g>
        <rect x="242" y="110" width="170" height="160" rx="10" fill="var(--surface)" stroke="var(--border-2)"/>
        <text x="258" y="134" fontFamily="var(--sans)" fontSize="12" fontWeight="600" fill="var(--text)">Raw signals</text>
        <text x="258" y="152" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">code · policy · graph</text>
        <text x="258" y="196" fontFamily="var(--mono)" fontSize="26" fontWeight="600" fill="var(--text)" letterSpacing="-0.02em">1,017</text>
        <text x="258" y="214" fontFamily="var(--mono)" fontSize="9" letterSpacing="1.2" fill="var(--text-4)">BEFORE CORRELATION</text>
        <line x1="258" y1="234" x2="394" y2="234" stroke="var(--border)"/>
        <text x="258" y="250" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">most are noise</text>
        <rect x="422" y="140" width="138" height="100" rx="10" fill="var(--surface-2)" stroke="var(--teal)" strokeOpacity="0.45"/>
        <text x="438" y="164" fontFamily="var(--sans)" fontSize="11" fontWeight="600" fill="var(--text)">Correlated + ranked</text>
        <text x="438" y="182" fontFamily="var(--mono)" fontSize="9" letterSpacing="0.3" fill="var(--text-4)">CODE ↔ INFRA ↔ THREAT</text>
        <rect x="438" y="198" width="100" height="3" fill="var(--red)" opacity="0.85"/>
        <rect x="438" y="208" width="82" height="3" fill="var(--red)" opacity="0.7"/>
        <rect x="438" y="218" width="60" height="3" fill="var(--amber)" opacity="0.7"/>
        <rect x="438" y="228" width="38" height="3" fill="var(--blue)" opacity="0.55"/>
        <rect x="572" y="150" width="86" height="80" rx="10" fill="var(--surface-2)" stroke="var(--teal)"/>
        <text x="588" y="194" fontFamily="var(--mono)" fontSize="30" fontWeight="600" fill="var(--teal)" letterSpacing="-0.02em">12</text>
        <text x="615" y="216" textAnchor="middle" fontFamily="var(--mono)" fontSize="8" letterSpacing="0.6" fill="var(--text-2)">WORTH FIXING</text>
        <circle cx="650" cy="160" r="3" fill="var(--teal)"/><circle cx="650" cy="160" r="10" fill="var(--teal)" opacity="0.18"/>
        <path d="M 220 190 L 242 190" stroke="url(#fline2V2)" strokeWidth="1.25" fill="none"/>
        <path d="M 412 190 L 422 190" stroke="url(#fline2V2)" strokeWidth="1.25" fill="none"/>
        <path d="M 560 190 L 572 190" stroke="url(#fline2V2)" strokeWidth="1.25" fill="none"/>
        <text x="30" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">collected continuously</text>
        <text x="242" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">mostly noise, suppressed</text>
        <text x="422" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--text-3)">chains, not alerts</text>
        <text x="572" y="352" fontFamily="var(--mono)" fontSize="10" fill="var(--teal)">your morning</text>
      </svg>
    </div>
  );
}

function FunnelV2() {
  const rows = [
    { chip:"CODE", cls:"", main:"GitHub app with four scan engines, fix PRs, and repo mapping", sub:"every repo mapped to the image and workload it deploys", count:"feeds graph" },
    { chip:"AWS", cls:"aws", main:"IAM, S3, EC2, RDS, Lambda, EKS, KMS, CloudTrail", sub:"read-only · onboards via role ARN · zero agents", count:"feeds graph" },
    { chip:"AZURE", cls:"azure", main:"Entra ID, Storage, VMs, AKS, Key Vault, Defender, Monitor", sub:"read-only · app registration · multi-tenant", count:"feeds graph" },
    { chip:"RISK", cls:"", main:"One risk engine weighing reach, identity, blast radius, and exploitability", sub:"one score across code, AWS + Azure, refreshed every 90s", count:"→ ranked" },
    { chip:"OUT", cls:"", main:"Slack, Teams, Email, fix PRs, dashboards", sub:"only the ranked exposures, not the noise", count:"—" },
  ];
  return (
    <section id="coverage" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 02 — The funnel</span></div>
            <h2>Four products, one graph, one short list.</h2>
            <p style={{marginTop:12}}>This is where the integration shows. Your repos, cloud accounts, and identity all feed the same graph. Code findings, vulnerabilities, misconfigurations, threats, and compliance gaps land in one funnel and come out the other end as a single ranked list. No jumping between tools, no spreadsheets to stitch it together.</p>
          </div>
          <div className="micro" style={{color:"var(--text-3)"}}>38,204 → 1,017 → 12</div>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, alignItems:"stretch"}}>
          <ArchitectureDiagramV2 />
          <FunnelDiagramV2 />
        </div>
        <div className="cov-list" style={{marginTop:20}}>
          {rows.map((r,i)=>(
            <div key={i} className="cov-row">
              <span className={"chip " + r.cls}>{r.chip}</span>
              <div><div className="main">{r.main}</div><div className="sub">{r.sub}</div></div>
              <span className="count">{r.count}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PipelineV2() {
  return (
    <section id="pipeline" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 03 — How it works</span></div>
            <h2>From cloud event to ranked exposure, in under a minute.</h2>
            <p style={{marginTop:12}}>For posture, compliance, and code we stay read-only. Nothing to install, no logs to ship, no month-long rollouts. AtmosGuard reads what your cloud and your repos already record, builds a map of what reaches what, and ranks each exposure by what it would actually cost you if it broke. Sentry goes one step deeper. For customers on that product we install lightweight agents, so live attacks are seen the moment they happen.</p>
          </div>
          <div className="micro" style={{color:"var(--text-3)"}}>CONNECT → INGEST → CORRELATE → RESPOND</div>
        </div>
        <div className="pipeline">
          <Step num="01" title="Connect" desc="Read-only role in AWS, app registration in Azure, GitHub app for your repos. Agents are installed only for Sentry customers. No data leaves your cloud boundary unencrypted." chips={["role ARN","SAML","github app"]} />
          <Step num="02" title="Ingest" desc="CloudTrail, Azure Activity, Config, identity, network state, and scan results from every repo, pulled continuously. Snapshots and diffs, not log forwarding." chips={["snapshots","diffs","scans"]} />
          <Step num="03" title="Correlate" desc="Every resource, identity, repo, and edge assembled into one graph. A code flaw, the resource it exposes, and the threat hitting it become one chain with one score, not three alerts in three tools." chips={["graph","correlate","score"]} active />
          <Step num="04" title="Respond" desc="Findings post to Slack or Teams with the resource, the identity, and the call attached. High-risk chains get a gated AI workup and a fix PR your team reviews. Signed evidence on the way out." chips={["slack","fix PR","attest"]} />
        </div>
      </div>
    </section>
  );
}

function RoadmapV2() {
  const byState = (s) => PRODUCTS.filter((p) => p.roadmap === s).map((p) => ({ name: p.name + " · " + p.category, state: s }));
  const phases = [
    { n:"01", period:"H1 2026", pill:{cls:"teal", label:"LIVE"}, items: byState("live") },
    { n:"02", period:"Now", pill:{cls:"teal", label:"BETA"}, items: byState("beta") },
    { n:"03", period:"Next", pill:{cls:"amber", label:"SOON"}, items: byState("soon") },
    { n:"04", period:"Horizon", pill:{cls:"muted", label:"HORIZON"}, items:[
      { name:"SOAR", state:"planned" },
      { name:"EDR / XDR", state:"planned" },
    ]},
  ];
  const dotColor = (s) => s === "live" || s === "beta" ? "var(--teal)" : s === "soon" ? "var(--amber)" : "var(--text-4)";
  const dotGlow = (s) => s === "live" ? "0 0 0 3px color-mix(in oklab, var(--teal) 22%, transparent)" : "none";
  const itemColor = (s) => s === "live" || s === "beta" ? "var(--text)" : s === "soon" ? "var(--text-2)" : "var(--text-3)";
  const tag = (s) => s === "soon" ? <span className="mono" style={{fontSize:9.5, color:"var(--amber)", letterSpacing:"0.08em", marginLeft:"auto"}}>SOON</span>
    : s === "beta" ? <span className="mono" style={{fontSize:9.5, color:"var(--teal)", letterSpacing:"0.08em", marginLeft:"auto"}}>BETA</span> : null;
  return (
    <section id="roadmap" className="section">
      <div className="wrap-wide">
        <div className="section-head">
          <div className="lead">
            <div className="rail-title"><span className="micro">§ 05 — Roadmap</span></div>
            <h2>What we&rsquo;re shipping, in order.</h2>
            <p style={{marginTop:12}}>Atlas is live. Forge is in private beta. Sentry and Stratos are next. Past that, the lanes our pilot teams are asking about. Items here are committed, not rumored.</p>
          </div>
          <div className="micro" style={{color:"var(--text-3)"}}>4 PHASES</div>
        </div>
        <div style={{display:"grid", gridTemplateColumns:"repeat(4, 1fr)", marginTop:28, border:"1px solid var(--border)", borderRadius:12, overflow:"hidden", background:"var(--surface)"}}>
          {phases.map((p,i)=>(
            <div key={i} style={{padding:"22px 22px 26px", borderRight: i<phases.length-1 ? "1px solid var(--border)" : "0", display:"flex", flexDirection:"column", gap:14, minHeight:220}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                <span className="mono" style={{fontSize:11, color:"var(--text-4)", letterSpacing:"0.08em"}}>{p.n}</span>
                <span className={"pill " + p.pill.cls}>{p.pill.label}</span>
              </div>
              <div className="mono" style={{fontSize:22, color:"var(--text)", letterSpacing:"-0.01em", fontFeatureSettings:'"tnum"'}}>{p.period}</div>
              <ul style={{listStyle:"none", padding:0, margin:"4px 0 0", display:"flex", flexDirection:"column", gap:10}}>
                {p.items.map((item,j)=>(
                  <li key={j} style={{display:"flex", alignItems:"center", gap:10, fontSize:13.5, color:itemColor(item.state)}}>
                    <span style={{width:7, height:7, borderRadius:"50%", background:dotColor(item.state), boxShadow:dotGlow(item.state), flexShrink:0}}/>
                    <span>{item.name}</span>
                    {tag(item.state)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* Override HomePage with the new composition (Frameworks reused from components.jsx). */
function HomePage() {
  return (<>
    <HeroV2 />
    <StatsV2 />
    <LensesV2 />
    <FunnelV2 />
    <PipelineV2 />
    <Frameworks />
    <RoadmapV2 />
  </>);
}

Object.assign(window, {
  HeroV2, StatsV2, LensesV2, FunnelV2, PipelineV2, RoadmapV2, CorrelationConsole, BoardReport,
  HomePage,
});
